import {
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
} from '../../components/WorkspaceDrawer/Settings/types';
import {type BooleanDynamicConfig} from '../useDynamicConfig';
import {
  type PrimaryLineType,
  useLinePlotDynamicConfigStore,
} from './stores/LinePlotDynamicConfigStore';

/**
 * Public readers for line-plot Statsig dynamic configs. The real evaluation
 * lives in `./stores/LinePlotDynamicConfigStore` (hydrated once per route via
 * `useInitializeLinePlotDynamicConfigStore`); these hooks are thin accessors
 * over that precomputed snapshot so panels don't re-run the cross-org
 * ranking/merging on every render.
 */

export {
  PRIMARY_LINE_TYPES,
  type PrimaryLineType,
} from './stores/LinePlotDynamicConfigStore';

/**
 * Gated reader for the org-level `primaryLineType`. Returns `'full-fidelity'`
 * whenever `enableMinMaxLastMode` is off, otherwise the cross-org-resolved
 * value from the hydrated store.
 *
 * Use this everywhere unless you explicitly need ungated access. The gate
 * exists so that an org which has `primaryLineType` configured but not
 * `enableMinMaxLastMode: true` is treated as "feature not yet rolled out"
 * rather than silently activating advanced bucketing strategies.
 */
export const usePrimaryLineType = (): PrimaryLineType =>
  useLinePlotDynamicConfigStore(state =>
    state.isMinMaxLastModeEnabled ? state.primaryLineType : 'full-fidelity'
  );

/**
 * Ungated escape hatch. Returns the cross-org-resolved `primaryLineType`
 * regardless of the `enableMinMaxLastMode` gate. Use only when the caller
 * explicitly needs to observe the configured value (e.g., admin tooling,
 * telemetry, gate-evaluation logic itself).
 */
export const usePrimaryLineTypeRaw = (): PrimaryLineType =>
  useLinePlotDynamicConfigStore(state => state.primaryLineType);

/**
 * Returns whether min/max/last bin visualization mode is enabled via the
 * `enableMinMaxLastMode` flag on the route-hydrated line plot dynamic-config
 * snapshot.
 */
export const useEnableMinMaxLastMode = (): BooleanDynamicConfig => {
  const value = useLinePlotDynamicConfigStore(
    state => state.isMinMaxLastModeEnabled
  );
  return {value, loading: false, error: undefined};
};

/**
 * Derives a `PrimaryLineType` from a `fullFidelityMode` setting value, gated
 * on `enableMinMaxLastMode`. Unlike `usePrimaryLineType` (which aggregates an
 * org-level Statsig config across the viewer's organizations), this hook's
 * input is the `fullFidelityMode` the caller already holds in hand - typically
 * the cascaded panel/workspace setting. Only two values are produced:
 * `'min-max-last'` when the gate is open AND `fullFidelityMode` is explicitly
 * `'min-max-last'`, and `'full-fidelity'` otherwise.
 */
export const useFullFidelityModeLineType = (
  fullFidelityMode: FullFidelityMode | undefined,
  isGrouped = false
) => {
  const {value: enableMinMaxLastMode} = useEnableMinMaxLastMode();
  // Grouped panels always render as Band. min-max-last draws each bucket's
  // min/max at their tracked x-positions (XAtMin/XAtMax), but across grouped
  // runs those positions are decoupled from the run that produced the
  // extreme (see groupsketch mergeStep), so the anchors are unreliable. Band
  // mode needs no positions, so force full-fidelity regardless of gate/mode.
  if (isGrouped) {
    return 'full-fidelity';
  }
  if (
    enableMinMaxLastMode &&
    fullFidelityMode === FULL_FIDELITY_MODES.MinMaxLast
  ) {
    return 'min-max-last';
  }
  return 'full-fidelity';
};

export const useLinePlotMaxRunsDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(state => state.linePlotMaxRuns);
};

export const useFullScreenLinePlotMaxRunsDynamicConfig = () => {
  return useLinePlotDynamicConfigStore(
    state => state.fullScreenLinePlotMaxRuns
  );
};
